// ========================================
// AEG Stock Manager - Main App
// ========================================
import React, { useState } from 'react';
import Dashboard from './components/Dashboard';
import WelcomeScreen from './components/WelcomeScreen';
import LoginScreen from './components/LoginScreen';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useInventory } from './hooks/useInventory';
import { aiAnalyst } from './utils/aiAnalyst';
import { Loader2, CloudOff } from 'lucide-react';

/**
 * רכיב הליבה - מנהל את הניתוב בין מסכים
 */
function AppContent() {
  const { user, isLoading: authLoading } = useAuth();
  // שליפה אוטומטית מהענן
  const { items: cloudItems, isLoading: inventoryLoading, error: inventoryError, refetch } = useInventory(user);

  // ניהול מצב מקומי (למקרה של העלאת קובץ ידנית או אחרי שקיבלנו נתונים מהענן)
  const [localData, setLocalData] = useState(null);
  const [insights, setInsights] = useState(null);

  // נתונים לתצוגה: או מהענן או מקומי
  const displayData = localData || cloudItems;

  // פונקציה לטיפול בנתונים חדשים (מקובץ או מהענן)
  const handleDataReady = (data) => {
    // 1. ניתוח מול תמונת מצב קודמת
    const newInsights = aiAnalyst.analyze(data);
    setInsights(newInsights);

    // 2. שמירת תמונת מצב נוכחית (רק אם המקור הוא קובץ אקסל חדש, כדי לא לדרוס היסטוריה עם אותם נתונים)
    // אבל לצורך הפשטות נשמור תמיד
    aiAnalyst.saveSnapshot(data);
  };

  // אפקט לעדכון Insights כשיש נתונים מהענן
  React.useEffect(() => {
    if (cloudItems) {
      handleDataReady(cloudItems);
    }
  }, [cloudItems]);

  // מסך טעינה (אימות או שליפת נתונים)
  if (authLoading || (user && inventoryLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-aeg-red animate-spin mx-auto mb-4" />
          <p className="text-slate-400 text-sm">
            {authLoading ? 'מאמת פרטים...' : 'טוען נתונים מהענן...'}
          </p>
        </div>
      </div>
    );
  }

  // לא מחובר -> מסך התחברות
  if (!user) {
    return <LoginScreen />;
  }

  // אם יש שגיאה בטעינה מהענן ואין נתונים מקומיים
  if (inventoryError && !localData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-900">
        <div className="text-center p-8 max-w-md">
          <CloudOff className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">שיבוש בתקשורת</h2>
          <p className="text-slate-500 mb-6">{inventoryError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            נסה שוב
          </button>
          <div className="mt-8 border-t pt-8">
            <p className="text-sm text-slate-400 mb-4">או נסה להעלות קובץ ידנית:</p>
            <WelcomeScreen onDataLoaded={(data) => setLocalData(data)} />
          </div>
        </div>
      </div>
    );
  }

  // מחובר, אין נתונים בענן ואין מקומי -> מסך העלאת קובץ (WelcomeScreen)
  if (!displayData) {
    return <WelcomeScreen onDataLoaded={(data) => {
      setLocalData(data);
      handleDataReady(data);
    }} />;
  }

  // מחובר + יש נתונים -> דשבורד
  return (
    <Dashboard
      initialData={displayData}
      onReset={() => {
        setLocalData(null);
        refetch(); // ננסה לטעון מחדש מהענן
      }}
      insights={insights}
    />
  );
}

/**
 * עטיפת האפליקציה עם AuthProvider
 */
function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen text-white">
        <AppContent />
      </div>
    </AuthProvider>
  );
}

export default App;
